import Block, { Theme } from '@/components/Block'
import Button, { ButtonTheme } from '@/components/Button'
import IcoMoon from '@/components/IcoMoon'
import { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { coreLayoutContainer, coreLayoutSection, UnitSize } from '@/constants/measured-scope'
import { SectionNavigationAction } from '@/shared/api-generated-types'
import { useMediaQuery } from '@react-hookz/web'
import * as React from 'react'
import { useMemo, useRef } from 'react'
import RSSlider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import * as styles from './style'

type CarouselOptions = false | UnitSize[] | 'always'

export interface CardRowProps {
  title?: string | null
  subtitle?: string | null
  theme?: Theme
  carousel?: CarouselOptions
  spacing?: UnitSize
  // Callback to parent with page to load if applicable
  className?: string
  navigationActions?: SectionNavigationAction[]
  children: React.ReactNode[]
  instructorCard?: boolean
  categoryCard?: boolean
}

// Which button themes should be used
// with the block theme that is selected
const buttonThemeMap: { [x: string]: ButtonTheme } = {
  primary: 'white',
  secondary: 'glass',
  tertiary: 'white',
  white: 'primaryOutline',
}

export type DeviceSizes = {
  [key in UnitSize]: boolean | undefined
}

const CardRow = ({
  title,
  subtitle,
  theme = 'white',
  navigationActions,
  children,
  instructorCard = false,
  categoryCard = false,
}: CardRowProps): JSX.Element => {
  const rsSliderRef = useRef<any>(null)
  const isSmallDevice = useMediaQuery(`only screen and (min-width : 0)`, { initializeWithValue: false })

  const carouselArrows = useMemo(() => {
    const prev = (): void => {
      if (rsSliderRef.current && rsSliderRef.current.slickPrev) {
        rsSliderRef.current.slickPrev()
      }
    }
    const next = (): void => {
      if (rsSliderRef.current && rsSliderRef.current.slickNext) {
        rsSliderRef.current.slickNext()
      }
    }
    return (
      <div className="flex-center navArrows">
        {!!navigationActions && isSmallDevice && (
          <>
            {navigationActions.map((item) => {
              return (
                <div key={item.label} className="ta-c mr-xxs-gte-lg">
                  <Button
                    label={item.label}
                    href={`/${item.targetPageId}`}
                    component="link"
                    theme={buttonThemeMap[theme]}
                  />
                </div>
              )
            })}
          </>
        )}
        <a className={`${styles.rowArrow} mr-xxs`} onClick={prev}>
          <IcoMoon icon="chevron-left" />
        </a>
        <a className={styles.rowArrow} onClick={next}>
          <IcoMoon icon="chevron-right" />
        </a>
      </div>
    )
  }, [isSmallDevice])

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 6,
    variableWidth: false,
    arrows: false,
    responsive: [
      instructorCard || categoryCard
        ? {
            //Below 1600
            breakpoint: 4000,
            settings: {
              slidesToShow: 6,
              slidesToScroll: 6,
            },
          }
        : {
            breakpoint: 4000,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 5,
            },
          },

      instructorCard || categoryCard
        ? {
            //Below 1600
            breakpoint: 1880,
            settings: {
              slidesToShow: 6,
              slidesToScroll: 6,
            },
          }
        : {
            breakpoint: 1880,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 5,
            },
          },

      instructorCard || categoryCard
        ? {
            //Below 1600
            breakpoint: 1600,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 5,
            },
          }
        : {
            breakpoint: 1600,
            settings: {
              slidesToShow: 4,
              slidesToScroll: 4,
            },
          },

      instructorCard || categoryCard
        ? {
            //Below 1280
            breakpoint: 1280,
            settings: {
              slidesToShow: 4,
              slidesToScroll: 4,
            },
          }
        : {
            breakpoint: 1280,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 3,
            },
          },
      instructorCard || categoryCard
        ? {
            //Below 980
            breakpoint: 980,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 3,
            },
          }
        : {
            breakpoint: 980,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 2,
            },
          },
      instructorCard || categoryCard
        ? {
            //Below 600
            breakpoint: 600,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 2,
            },
          }
        : {
            breakpoint: 600,
            settings: {
              slidesToShow: 1.2,
              slidesToScroll: 1,
            },
          },
    ],
  }

  return (
    <Block className={`${coreLayoutSection} ${styles.root}`}>
      <Stack className={coreLayoutContainer}>
        <Stack alignItems="center" x>
          {title && (
            <Text tag="h2" weight="lg" size="lg" responsiveSize={{ xl: 'xl' }}>
              {title}
            </Text>
          )}
          {carouselArrows}
        </Stack>
        {subtitle && (
          <Text tag="p" size="xs" weight="sm" className="fc-grey">
            {subtitle}
          </Text>
        )}
        <RSSlider
          {...sliderSettings}
          ref={rsSliderRef}
          className="pt-xxs pb-sm pt-xs-gte-sm pb-lg-gte-sm pt-sm-gte-lg pt-md-gte-xl"
        >
          {children.map((card, key) => (
            <div key={key}>{card}</div>
          ))}
        </RSSlider>
      </Stack>
    </Block>
  )
}

export default CardRow
